/** Client-facing Outcome labels for the NYCBS External PA Requests table. */
export enum NycbsClientOutcome {
  AUTHORIZED = "Authorized",
  AUTH_NOT_REQUIRED = "Auth Not Required",
  DENIED = "Denied",
  ONSHORE = "Onshore",
  WAITING_FOR_DETERMINATION = "Waiting for Determination",
  CALL_REQUIRED = "Call Required",
  RISA_IN_PROGRESS = "RISA In Progress",
}

/** Empty-string filter value matches null/empty response_status in BigQuery. */
export const NYCBS_RISA_IN_PROGRESS_FILTER_VALUE = "";

/** Maps each client Outcome filter value to internal response_status values. */
export const NYCBS_CLIENT_OUTCOME_TO_INTERNAL_STATUSES: Record<
  NycbsClientOutcome,
  string[]
> = {
  [NycbsClientOutcome.AUTHORIZED]: ["Approved", "Approval on file"],
  [NycbsClientOutcome.AUTH_NOT_REQUIRED]: ["Auth Not Required"],
  [NycbsClientOutcome.DENIED]: ["Denied", "Denial on file", "Drug Not Covered"],
  [NycbsClientOutcome.ONSHORE]: ["Onshore Assistance"],
  [NycbsClientOutcome.WAITING_FOR_DETERMINATION]: ["Pending"],
  [NycbsClientOutcome.CALL_REQUIRED]: [
    "Not Correct Processor",
    "NA Outcome",
    "Case on file",
    "Case on File",
    "Non-ePA",
    "PA Resolved",
    "Send To Client",
  ],
  [NycbsClientOutcome.RISA_IN_PROGRESS]: [NYCBS_RISA_IN_PROGRESS_FILTER_VALUE],
};

/** Internal response_status values that map to the client-facing Denied outcome. */
export const NYCBS_DENIED_INTERNAL_STATUSES =
  NYCBS_CLIENT_OUTCOME_TO_INTERNAL_STATUSES[NycbsClientOutcome.DENIED];

/**
 * Returns true when the raw response_status maps to the Denied client outcome.
 *
 * @param responseStatus - Raw response_status from the order row
 * @return boolean: True when the row outcome is Denied
 */
export const isNycbsDeniedResponseStatus = (
  responseStatus: string | null | undefined,
): boolean => {
  if (responseStatus == null) {
    return false;
  }

  const normalized = responseStatus.trim();
  if (!normalized || normalized.toLowerCase() === "null") {
    return false;
  }

  return NYCBS_DENIED_INTERNAL_STATUSES.some(
    (status) => status.toLowerCase() === normalized.toLowerCase(),
  );
};

const INTERNAL_STATUS_TO_CLIENT_OUTCOME: Record<string, NycbsClientOutcome> = {
  Approved: NycbsClientOutcome.AUTHORIZED,
  "Approval on file": NycbsClientOutcome.AUTHORIZED,
  "Auth Not Required": NycbsClientOutcome.AUTH_NOT_REQUIRED,
  Denied: NycbsClientOutcome.DENIED,
  "Denial on file": NycbsClientOutcome.DENIED,
  "Drug Not Covered": NycbsClientOutcome.DENIED,
  "Onshore Assistance": NycbsClientOutcome.ONSHORE,
  Pending: NycbsClientOutcome.WAITING_FOR_DETERMINATION,
  "Not Correct Processor": NycbsClientOutcome.CALL_REQUIRED,
  "NA Outcome": NycbsClientOutcome.CALL_REQUIRED,
  "Case on file": NycbsClientOutcome.CALL_REQUIRED,
  "Case on File": NycbsClientOutcome.CALL_REQUIRED,
  "Non-ePA": NycbsClientOutcome.CALL_REQUIRED,
  "PA Resolved": NycbsClientOutcome.CALL_REQUIRED,
  "Send To Client": NycbsClientOutcome.CALL_REQUIRED,
};

const OUTCOME_BADGE_STYLES: Record<
  NycbsClientOutcome,
  { color: string; bgColor: string }
> = {
  [NycbsClientOutcome.AUTHORIZED]: { color: "#005D49", bgColor: "#E6F3F0" },
  [NycbsClientOutcome.AUTH_NOT_REQUIRED]: { color: "#005D49", bgColor: "#E6F3F0" },
  [NycbsClientOutcome.DENIED]: { color: "#DC2626", bgColor: "#FEE2E2" },
  [NycbsClientOutcome.ONSHORE]: { color: "#7C3AED", bgColor: "#EDE9FE" },
  [NycbsClientOutcome.WAITING_FOR_DETERMINATION]: {
    color: "#665D00",
    bgColor: "#FFFBEB",
  },
  [NycbsClientOutcome.CALL_REQUIRED]: { color: "#C2410C", bgColor: "#FFEDD5" },
  [NycbsClientOutcome.RISA_IN_PROGRESS]: { color: "#665D00", bgColor: "#FFFBEB" },
};

/**
 * Returns true when the client-facing Outcome label is Denied.
 *
 * @param clientOutcomeLabel - Outcome label shown in the Outcome column
 * @return boolean: True when the label represents a denied outcome
 */
export const isNycbsDeniedClientOutcomeLabel = (
  clientOutcomeLabel: string,
): boolean => {
  return (
    clientOutcomeLabel.trim().toLowerCase() ===
    NycbsClientOutcome.DENIED.toLowerCase()
  );
};

/**
 * Maps raw response_status to the client-facing Outcome label.
 *
 * @input responseStatus - Raw response_status from the order row
 * @return string: Client-facing Outcome label
 */
export const getNycbsClientOutcomeDisplayText = (
  responseStatus: string | null | undefined,
): string => {
  if (responseStatus == null) {
    return NycbsClientOutcome.RISA_IN_PROGRESS;
  }

  const normalized = responseStatus.trim();
  if (!normalized || normalized.toLowerCase() === "null") {
    return NycbsClientOutcome.RISA_IN_PROGRESS;
  }

  const exactMatch = INTERNAL_STATUS_TO_CLIENT_OUTCOME[normalized];
  if (exactMatch) {
    return exactMatch;
  }

  const caseInsensitiveMatch = Object.entries(
    INTERNAL_STATUS_TO_CLIENT_OUTCOME,
  ).find(([status]) => status.toLowerCase() === normalized.toLowerCase());

  if (caseInsensitiveMatch) {
    return caseInsensitiveMatch[1];
  }

  return NycbsClientOutcome.CALL_REQUIRED;
};

/**
 * Returns badge colors for a client-facing Outcome label.
 *
 * @input outcomeLabel - Client-facing Outcome text
 * @return object: Text and background colors for the badge
 */
export const getNycbsClientOutcomeBadgeStyles = (
  outcomeLabel: string,
): { color: string; bgColor: string } => {
  const styles =
    OUTCOME_BADGE_STYLES[outcomeLabel as NycbsClientOutcome] ??
    OUTCOME_BADGE_STYLES[NycbsClientOutcome.CALL_REQUIRED];
  return styles;
};

/**
 * Returns true when the row Outcome is RISA In Progress (unset response_status).
 *
 * @input responseStatus - Raw response_status from the order row
 * @return boolean: True when response_status is null or empty
 */
export const isNycbsRisaInProgress = (
  responseStatus: string | null | undefined,
): boolean => {
  return responseStatus == null || responseStatus === "";
};

/**
 * Expands selected client Outcome filter values into internal response_status values.
 *
 * @input selectedClientOutcomes - Client Outcome values chosen in the filter sidebar
 * @return string[]: Internal response_status values for the API filter
 */
export const expandClientOutcomeFiltersToInternalStatuses = (
  selectedClientOutcomes: string[],
): string[] => {
  const internalStatuses = new Set<string>();

  selectedClientOutcomes.forEach((clientOutcome) => {
    const mapped =
      NYCBS_CLIENT_OUTCOME_TO_INTERNAL_STATUSES[
        clientOutcome as NycbsClientOutcome
      ];
    if (mapped) {
      mapped.forEach((status) => internalStatuses.add(status));
    }
  });

  return Array.from(internalStatuses);
};
